import React, { useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import { useModal } from 'components/modals/ModalContext';
import { useSettings } from 'services/settings/SettingsContext';
import { convertUTCToLocalTimeString } from 'internationalization/timezoneConversions';
import JobModal from './JobModal';
import '../../style/scss/modals.scss';

interface ProjectTimeRowDateGroup {
    date: string;
    rows: {
        user_id: number;
        full_name: string;
        time_type_name?: string;
        start_datetime?: string;
        end_datetime?: string;
        break_duration?: number;
        duration?: number;
        job_id?: number;
        job_number?: string;
        is_missing_hours?: boolean;
    }[];
};

const ProjectTimeRowList: React.FC<{ data: any }> = ({ data }) => {
    const { t } = useTranslation();
    const { initializeModal } = useModal();
    const { userLocale, userTimezone, getTimeFormat} = useSettings();
    const is12HourFormat = getTimeFormat() === '12h';

    // Format the duration
    const formatDuration = (hours: number) => {
        if (!hours) return "0:00";
        const totalMinutes = Math.round(hours * 60);
        const h = Math.floor(totalMinutes / 60);
        const m = totalMinutes % 60;
        return `${h}:${m.toString().padStart(2, '0')}`;
    };

    // Format the date with written out day name and month
    const formatDate = (dateString: string) => {
        const date = new Date(dateString);
        return date.toLocaleDateString(userLocale, {
            weekday: 'long',
            day: 'numeric',
            month: 'long',
            year: 'numeric'
        });
    };

    // Render the start and end time
    const renderTimeRange = (row: any) => {
        const duration = formatDuration(row.duration);
        const breakDuration = row.break_duration ? formatDuration(row.break_duration) : null;

        // Render just duration and break
        if (!row.start_datetime || !row.end_datetime) {
            return (
                <span>
                    {duration} {t('date_time.general.hour.singular')}
                    {breakDuration ? ` · ${t('timesheets.general.break_uncapitalized_label')} ${breakDuration}` : ''}
                </span>
            );
        }

        // Convert the start and end times to the local timezone
        const start = convertUTCToLocalTimeString(row.start_datetime, userLocale, userTimezone, is12HourFormat);
        const end = convertUTCToLocalTimeString(row.end_datetime, userLocale, userTimezone, is12HourFormat);

        // Render start, end, duration and break
        return (
            <span>
                {start} – {end} ({duration} {t('date_time.general.hour.singular')}{breakDuration ? `, ${t('timesheets.general.break_uncapitalized_label')} ${breakDuration}` : ''})
            </span>
        );
    };

    // Group the timerows per appointment day, or show which user still need to registrate time
    const groupedDays = useMemo(() => {
        if (!data) {
            return [];
        };

        const today = new Date();
        today.setHours(23, 59, 59, 999);

        // Initialize maps to store the date groups and users
        const grouped: Record<string, ProjectTimeRowDateGroup> = {};
        const userMap: Record<number, any> = {};

        // Loop through the appointments of the jobs linked to the project
        data.jobs?.forEach((job: any) => {
            job.appointments?.forEach((appointment: any) => {

                // Get the date of the appointment
                const date = appointment.start_datetime?.split('T')[0];

                // Loop through the appointments to create the dates on which hours should be registrated
                if (date && new Date(date) <= today && !appointment.deleted) {

                    // Add the date group if it does not yet exist
                    if (!grouped[date]) {
                        grouped[date] = { date, rows: [] };
                    }

                    // Loop through the assignees of the appointments
                    appointment.assignees?.forEach((assignee: any) => {

                        // Save the assignee name in the user map
                        userMap[assignee.id] = assignee;

                        // Determine wheter the user is already added to this day
                        const existingRow = grouped[date].rows.find((row) => row.user_id === assignee.id && row.job_id === job.id);

                        // When the user is not added to this day, add his name. This user should have hours registrated
                        if (!existingRow) {
                            grouped[date].rows.push({
                                user_id: assignee.id,
                                full_name: assignee.full_name,
                                job_id: job.id,
                                job_number: job.number,
                                is_missing_hours: true,
                            });
                        }
                    });
                }
            });
        });

        // Loop through the timerows added at a job linked to the project
        data.jobs?.forEach((job: any) => {
            job.timerows?.forEach((timerow: any) => {

                // Get the date of the timerow
                const date = timerow.date || timerow.start_datetime?.split('T')[0];

                if (date && new Date(date) <= today && !timerow.deleted) {
                    // Add the date group if it does not yet exist
                    if (!grouped[date]) {
                        grouped[date] = { date, rows: [] };
                    }

                    // Add the timerow to the day group
                    grouped[date].rows.push({
                        user_id: timerow.assignee,
                        full_name: timerow.assignee_name || userMap[timerow.assignee]?.full_name,
                        start_datetime: timerow.start_datetime,
                        end_datetime: timerow.end_datetime,
                        break_duration: Number(timerow.break_duration || 0),
                        duration: Number(timerow.duration || 0),
                        time_type_name: timerow.time_type_name,
                        job_id: job.id,
                        job_number: job.number,
                        is_missing_hours: false,
                    });
                }
            });
        });

        // Loop through the timerows added directly to a project
        data.project_time_rows?.forEach((timerow: any) => {

            // Get the date of the timerow
            const date = timerow.date || timerow.start_datetime?.split('T')[0];

            if (date && new Date(date) <= today && !timerow.deleted) {
                // Add the date group if it does not yet exist
                if (!grouped[date]) {
                    grouped[date] = { date, rows: [] };
                }

                // Add the timerow to the day group
                grouped[date].rows.push({
                    user_id: timerow.assignee,
                    full_name: timerow.assignee_name || userMap[timerow.assignee]?.full_name,
                    start_datetime: timerow.start_datetime,
                    end_datetime: timerow.end_datetime,
                    break_duration: Number(timerow.break_duration || 0),
                    duration: Number(timerow.duration || 0),
                    time_type_name: timerow.time_type_name,
                    is_missing_hours: false,
                });
            }
        });

        // Remove the missing hours rows of users that registrated hours on that date
        Object.values(grouped).forEach((day) => {
            const usersWithHours = new Set(
                day.rows
                    .filter((row) => !row.is_missing_hours)
                    .map((row) => Number(row.user_id))
            );
            day.rows = day.rows.filter(
                (row) => !(row.is_missing_hours && usersWithHours.has(Number(row.user_id)))
            );
        });

        // Sort the rows on alphabetical order and then on time within date groups
        Object.values(grouped).forEach((day) => {
            day.rows.sort((rowA, rowB) => {
                const nameCompare = (rowA.full_name || '').localeCompare(rowB.full_name || '');

                if (nameCompare !== 0) {
                    return nameCompare;
                }

                const timeA = rowA.start_datetime ? new Date(rowA.start_datetime).getTime() : 0;
                const timeB = rowB.start_datetime ? new Date(rowB.start_datetime).getTime() : 0;

                return timeA - timeB;
            });
        });

        // Return the groups chronologically
        return Object.values(grouped).sort((dayA, dayB) => {
            return new Date(dayA.date).getTime() - new Date(dayB.date).getTime();
        });
    }, [data]);

    // Handle open timesheet for direct project hours
    const handleOpenTimeSheet = (userId: number, date: string) => {

        // Get the start date of the given week
        const weekStart = new Date(date);
        const day = weekStart.getDay();
        const diff = day === 0 ? -6 : 1 - day;
        weekStart.setDate(weekStart.getDate() + diff);
        const formattedWeekStart = weekStart.toISOString().split('T')[0];

        // Open the timesheet
        window.open(`/user-hours/timerows?deleted=false&assignee_id=${userId}&page=1&user_id=${userId}&week_start=${formattedWeekStart}`, '_blank');
    };

    // Handle open the job
    const handleOpenJob = (jobId: number | null) => {
        if (!jobId) return;
        initializeModal(
            React.cloneElement(<JobModal />), { 
                itemId: jobId,
                modalSize: 'large' 
            } 
        );
    }

    if (!groupedDays.length) {
        return (
            <div className="card project-time-registration-list empty">
                {t('project.general.no_time_registrations')}
            </div>
        );
    }

    const totalDuration = groupedDays.reduce(
        (total, day) => {
            return total + day.rows.reduce(
                (dayTotal, row) =>
                    dayTotal + (row.duration || 0),
                0
            );
        },
        0
    );

    return (
        <div className="project-timerows">
            {groupedDays.map((day) => {
                return (
                    <div
                        key={day.date}
                        className='modal-list condensed' 
                    >
                        <div className="list-header">
                            {formatDate(day.date)}
                        </div>
                        {day.rows.map((row, index) => {
                            return (
                                <ul 
                                    className={`list-row ${!row.is_missing_hours ? 'clickable' : ''}`}
                                    style={{ gridTemplateColumns: '2fr 3.5fr 1.5fr 2fr' }}
                                    key={`${day.date}-${row.user_id}-${row.job_id}-${index}`}
                                    onClick={() => {
                                        if (!row.is_missing_hours) {
                                            if (row.job_id) {
                                                handleOpenJob(row.job_id);
                                            } else {
                                                handleOpenTimeSheet(row.user_id, day.date);
                                            }
                                        }
                                    }}
                                >
                                    <li>
                                        {row.full_name}
                                    </li>
                                    <li>
                                        {row.is_missing_hours ? (
                                            <span>
                                                {t('project.general.no_hours_registered')}
                                            </span>
                                        ) : (
                                            renderTimeRange(row)
                                        )}
                                    </li>
                                    <li>
                                        {row.time_type_name}
                                    </li>
                                    <li>
                                        {!row.is_missing_hours && row.job_number && (
                                            <>{t('timerow.general.job_label')} {row.job_number}</>
                                        )}
                                    </li>
                                </ul>
                            );
                        })}
                    </div>
                );
            })}
            {totalDuration > 0 && (
                <div className="modal-list total-row condensed">
                    <ul
                        className="list-row"
                        style={{ gridTemplateColumns: '2fr 3.5fr 1.5fr 2fr' }}
                    >
                        <li>{t('general.total')}</li>
                        <li>{formatDuration(totalDuration)} {t('date_time.general.hour.singular')}</li>
                        <li />
                        <li />
                    </ul>
                </div>
            )}
        </div>
    );
};

export default ProjectTimeRowList;